import type {
  VariantCampaignSelectFragment,
  VariantWithTemplateSelectFragment,
} from '~/api/graphql/fragments/variant.generated';
import type {
  CampaignSelectFragment,
  CampaignVersionSelectFragment,
} from '~/api/graphql/fragments/campaign.generated';
import type {
  CampaignStatus,
  CampaignType,
  VariantPriceStatus,
  VariantUrlResourceType,
} from '~/types/graphql';
import type { CampaignWithVariantTemplateSelectFragment } from '~/api/graphql/fragments/campaignTemplate.generated';

export type VariantCampaign = VariantCampaignSelectFragment;
export type Variant = VariantWithTemplateSelectFragment;
export type TabCampaignStatus = CampaignStatus | 'ALL';
export type TabCampaignType = CampaignType | 'ALL';
export enum TargetChannel {
  DIRECT = 'direct',
  EMAIL = 'email',
  REFERRAL = 'referral',
  ORGANIC_SOCIAL = 'organic-social',
  ORGANIC_SEARCH = 'organic-search',
  PAID_SOCIAL = 'paid-social',
  PAID_SEARCH = 'paid-search',
  SMS = 'sms',
  ALL = 'all',
}
export enum TargetDevice {
  ALL = 'all',
  DESKTOP = 'desktop',
  TABLET = 'tablet',
  MOBILE = 'mobile',
}
export enum TargetVisitorType {
  ALL = 'all',
  NEW = 'new',
  RETURNING = 'returning',
}

export enum ICampaignType {
  // CONTENT = 'CONTENT',
  PRICE = 'PRICE',
  TEMPLATE = 'TEMPLATE',
  TEMPLATE_BUNDLE = 'TEMPLATE_BUNDLE',
  URL = 'URL',
  // THEME = 'THEME',
}

export interface IBaseCampaignConditions {
  targetPageIds?: string[];
  targetMarkets?: string[];
}
export type IBaseCampaign = CampaignSelectFragment;

// The product a price variant was tested with, snapshotted into the version settings. Mirrors
// VariantPrice without its id, so it can stand in for the live price once the variant moved on.
export interface ICampaignVariantVersionProduct {
  baseID: string;
  handle: string;
  status: VariantPriceStatus;
  redirectURL?: string;
  title?: string;
}

// The page a URL variant redirected to, snapshotted into the version settings. Mirrors VariantUrl
// without its id, and the homepage keeps no record, so only the URL itself is always there.
export interface ICampaignVariantVersionDestination {
  baseID?: string;
  handle?: string;
  redirectURL?: string;
  resourceType?: VariantUrlResourceType;
  title?: string;
}

// Template fields are set by template campaigns, product by price ones and destination by URL ones —
// a version carries one of the three.
export interface ICampaignVariantVersion {
  templateSuffix?: string;
  templateThumbnailURL?: string;
  templateTitle?: string;
  trafficRate: number;
  product?: ICampaignVariantVersionProduct;
  destination?: ICampaignVariantVersionDestination;
}

export interface IBaseCampaignVersionSettings {
  conditions?: IBaseCampaignConditions;
  priority: string;
  targetChannels: TargetChannel[];
  targetCountries: string[];
  targetDevices: TargetDevice[];
  targetVisitorTypes: TargetVisitorType[];
  trafficRate: Record<string, number> | number;
  campaignVariants?: Record<string, ICampaignVariantVersion>;

  target_channels?: TargetChannel[];
  target_countries?: string[];
  target_devices?: TargetDevice[];
  target_visitor_types?: TargetVisitorType[];
  traffic_rate?: Record<string, number> | number;
  campaign_variants?: Record<string, ICampaignVariantVersion>;
}
export interface IBaseCampaignVersion extends CampaignVersionSelectFragment {
  settings?: IBaseCampaignVersionSettings;
}

export interface Campaign extends IBaseCampaign, CampaignWithVariantTemplateSelectFragment {
  conditions?: IBaseCampaignConditions;
  campaignVersionId?: string;
  campaignGroupVersionId?: string;
}
export type IVariantTemplate = VariantWithTemplateSelectFragment;

export enum IVariantType {
  VARIANT_A = 'A',
  VARIANT_B = 'B',
}
